import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import AppSafeArea from '@/components/layout/AppSafeArea';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AppHeader from '@/components/AppHeader';
import SeasonTemplateBuilder, {
  type SeasonBuilderInitialState,
  type SeasonBuilderResult,
} from '@/components/SeasonTemplateBuilder';
import { useToast } from '@/hooks/useToast';
import { trpc, useTRPCClient } from '@/lib/trpc';
import { isWeb } from '@/lib/platform';
import type { LeagueSeasonSummary } from '@/types/league';
import type { PickLockPolicyConfig } from '@shared/seasons';
import { normalizeStringParam } from '@/lib/routes';

const centsToInputString = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return (value / 100).toFixed(2);
};

const getSeasonScoringSystem = (season?: LeagueSeasonSummary) => {
  const config = (season?.scoringConfig as { legacyScoringSystem?: string } | null) ?? null;
  return config?.legacyScoringSystem === 'totalWins' ? 'totalWins' : 'oddsBased';
};

const getSeasonWeekMode = (season?: LeagueSeasonSummary) => {
  const config = (season?.scoringConfig as { seasonWeekMode?: string } | null) ?? null;
  return config?.seasonWeekMode === 'propWeeklyOnly' ? 'propWeeklyOnly' : 'standard';
};

const getSeasonPickLockPolicy = (season?: LeagueSeasonSummary): PickLockPolicyConfig => {
  const config = (season?.scoringConfig as { pickLockPolicy?: PickLockPolicyConfig } | null) ?? null;
  const policy = config?.pickLockPolicy;
  if (policy?.mode === 'roundStart') return { mode: 'roundStart' };
  if (policy?.mode === 'afterFirstNGames') {
    return { mode: 'afterFirstNGames', gamesCount: policy.gamesCount ?? 1 };
  }
  return { mode: 'gameStart' };
};

export default function SeasonEditorScreen() {
  const navigation = useNavigation();
  const params = useLocalSearchParams<{
    id?: string | string[];
    mode?: string | string[];
    seasonId?: string | string[];
  }>();
  const leagueId = normalizeStringParam(params.id) ?? '';
  const mode = normalizeStringParam(params.mode) === 'edit' ? 'edit' : 'create';
  const seasonId = normalizeStringParam(params.seasonId);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();
  const [initialState, setInitialState] = useState<SeasonBuilderInitialState | null>(null);
  const [lockedBetIds, setLockedBetIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const skipUnsavedPromptRef = useRef(false);
  const { data: seasons = [] } = useQuery<LeagueSeasonSummary[]>(
    trpc.seasons.listByLeague.queryOptions(
      { leagueId },
      { enabled: mode === 'edit' && leagueId.length > 0 },
    ) as any,
  );
  const season = mode === 'edit' ? seasons.find((candidate) => String(candidate.id) === seasonId) : undefined;

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (event) => {
      if (skipUnsavedPromptRef.current || !hasUnsavedChanges) {
        return;
      }

      event.preventDefault();

      const proceed = () => {
        skipUnsavedPromptRef.current = true;
        navigation.dispatch(event.data.action);
      };

      if (isWeb) {
        const confirmed =
          typeof window !== 'undefined' &&
          window.confirm('Discard unsaved changes?');
        if (confirmed) {
          proceed();
        }
        return;
      }

      Alert.alert('Discard changes?', 'You have unsaved changes. Do you want to leave?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Discard', style: 'destructive', onPress: proceed },
      ]);
    });

    return unsubscribe;
  }, [navigation, hasUnsavedChanges]);

  useEffect(() => {
    if (mode !== 'edit') {
      setInitialState(null);
      setLockedBetIds([]);
      setIsLoading(false);
      return;
    }
    if (!seasonId || !season) {
      toast.show({
        type: 'error',
        title: 'Season Not Found',
        description: 'Missing season details for editing.',
      });
      navigation.goBack();
      return;
    }

    let isMounted = true;
    const loadSeasonConfig = async () => {
      try {
        const config = await trpcClient.seasons.config.query({
          leagueId,
          seasonId: String(season.id),
        });
        if (!config.isEditable || !config.config) {
          toast.show({
            type: 'error',
            title: 'Not Editable',
            description: 'This season cannot be edited.',
          });
          navigation.goBack();
          return;
        }
        const nextState: SeasonBuilderInitialState = {
          templateId: config.config.templateId,
          label: season.label,
          scoringSystem: getSeasonScoringSystem(season),
          seasonWeekMode: getSeasonWeekMode(season),
          pickLockPolicy: getSeasonPickLockPolicy(season),
          weeklyBuyIn: centsToInputString(season.weeklyBuyInCents),
          seasonBuyIn: centsToInputString(season.seasonBuyInCents),
          isActive: season.isActive,
          weeks: config.config.weeks,
          additionalInfo: season.additionalInfo ?? null,
        };
        if (!isMounted) return;
        setInitialState(nextState);
        setLockedBetIds(config.lockedBetIds ?? []);
      } catch (error: any) {
        toast.show({
          type: 'error',
          title: 'Failed to load season',
          description: error?.message ?? 'Unable to load season configuration.',
        });
        navigation.goBack();
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadSeasonConfig();

    return () => {
      isMounted = false;
    };
  }, [leagueId, mode, navigation, season, seasonId, toast, trpcClient.seasons.config]);

  const createSeasonMutation = useMutation({
    mutationFn: async (result: SeasonBuilderResult) => {
      return trpcClient.seasons.create.mutate({
        leagueId,
        payload: {
          label: result.label,
          templateConfig: result.config,
          scoringConfig: {
            legacyScoringSystem: result.scoringSystem,
            seasonWeekMode: result.seasonWeekMode,
            pickLockPolicy: result.pickLockPolicy,
          },
          weeklyBuyIn: result.weeklyBuyIn,
          seasonBuyIn: result.seasonBuyIn,
          isActive: result.isActive,
          additionalInfo: result.additionalInfo ?? null,
        },
      });
    },
    onSuccess: async () => {
      toast.show({
        type: 'success',
        title: 'Season Created',
        description: 'New season created successfully.',
      });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId, 'seasons'] });
      queryClient.invalidateQueries(trpc.seasons.listByLeague.queryFilter({ leagueId }));
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId] });
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      skipUnsavedPromptRef.current = true;
      setHasUnsavedChanges(false);
      navigation.goBack();
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Season Creation Failed',
        description: error?.message ?? 'Failed to create the season. Please try again.',
      });
    },
  });

  const updateSeasonMutation = useMutation({
    mutationFn: async ({ seasonId, result }: { seasonId: string; result: SeasonBuilderResult }) => {
      const seasonPayload: Record<string, unknown> = {
        label: result.label,
        scoringConfig: { 
          legacyScoringSystem: result.scoringSystem,
          seasonWeekMode: result.seasonWeekMode,
          pickLockPolicy: result.pickLockPolicy,
        },
        isActive: result.isActive,
        additionalInfo: result.additionalInfo ?? null,
      };
      if (typeof result.weeklyBuyIn === 'string') {
        seasonPayload.weeklyBuyIn = result.weeklyBuyIn;
      }
      if (typeof result.seasonBuyIn === 'string') {
        seasonPayload.seasonBuyIn = result.seasonBuyIn;
      }
      await trpcClient.seasons.update.mutate({
        leagueId,
        seasonId,
        payload: seasonPayload,
      });
      await trpcClient.seasons.updateConfig.mutate({
        leagueId,
        seasonId,
        config: result.config,
      });
    },
    onSuccess: async () => {
      toast.show({
        type: 'success',
        title: 'Season Updated',
        description: 'Season configuration saved successfully.',
      });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId, 'seasons'] });
      queryClient.invalidateQueries(trpc.seasons.listByLeague.queryFilter({ leagueId }));
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId] });
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      skipUnsavedPromptRef.current = true;
      setHasUnsavedChanges(false);
      navigation.goBack();
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Update Failed',
        description: error?.message ?? 'Failed to update the season.',
      });
    },
  });

  const handleSubmit = async (result: SeasonBuilderResult) => {
    if (mode === 'edit') {
      if (!season) return;
      await updateSeasonMutation.mutateAsync({ seasonId: String(season.id), result });
      return;
    }
    await createSeasonMutation.mutateAsync(result);
  };

  if (isLoading) {
    return (
      <AppSafeArea style={styles.safeArea} edges={['left', 'right', 'bottom']}>
        <AppHeader title="Season Editor" currentLeagueId={leagueId} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" />
        </View>
      </AppSafeArea>
    );
  }

  return (
    <AppSafeArea style={styles.safeArea} edges={['left', 'right', 'bottom']}>
      <AppHeader title="Season Editor" currentLeagueId={leagueId} />
      <SeasonTemplateBuilder
        visible
        onClose={() => navigation.goBack()}
        onSubmit={handleSubmit}
        initialState={initialState}
        submitLabel={mode === 'edit' ? 'Save Season' : 'Create Season'}
        mode={mode}
        lockedBetIds={lockedBetIds}
        templateLocked={mode === 'edit'}
        presentation="screen"
        safeAreaEdges={[]}
        onDirtyChange={setHasUnsavedChanges}
        leagueId={leagueId}
        seasonId={seasonId}
      />
    </AppSafeArea>
  );
}

const styles = StyleSheet.create(theme => ({
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  safeArea: {
    backgroundColor: theme.colors.background,
  },
}));
